import { token } from '@atlas-frontend/design-tokens';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { Button, Checkbox, Divider, Flex } from 'antd';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import styled from 'styled-components';
import { ZodType } from 'zod/v3';
import { Bleed, Main, Padding, SidePanel } from '../../common/components';
import {
  confirmPanelFormClose,
  Form,
  FormField,
  FormRadioGroup,
  FormSelect,
  FormSubmitButton,
  useGetIdFromName,
} from '../../common/form';
import { FormDatePicker } from '../../common/form/input/FormDatePicker';
import { ValidationMessagesMap } from '../../common/utils/ValidationMessagesMap';
import { useSystemClassifierItem } from '../../configuration';
import { SystemClassifierFormSelect } from '../../configuration/SystemClassifierFormSelect';
import translations from '../validation/KeyBulkEditPanel.translations.json';
import editTranslations from '../validation/KeyEditPanel.translations.json';
import { BulkEditKey, EditableField } from './types';
import { useKeyClassifiers } from './useKeyClassifiers';
import {
  buildBulkSchema,
  bulkUpdateKeys,
  createDefaultBulkEdit,
  validateBulkDates,
} from './utils';

const { addTranslations, useTranslation } = i18nNsFactory(
  'key-bulk-edit-panel',
);
addTranslations(translations);
addTranslations(editTranslations);

export interface KeyBulkEditPanelProps {
  editable: Set<number>;
  indicatorStatus: boolean;
  onClose: () => void;
}

export function KeyBulkEditPanel(props: KeyBulkEditPanelProps) {
  const { editable, indicatorStatus, onClose } = props;
  const [schema, setSchema] = useState<ZodType>(() =>
    buildBulkSchema(new Set(), indicatorStatus),
  );
  const [fields, setFields] = useState<Set<EditableField>>(() => new Set());
  const [validationErrors, setValidationErrors] = useState<
    ValidationMessagesMap | undefined
  >();
  const { getValues, setValue } = useFormContext();
  const recipientIds = useWatch({ name: 'recipientIds' });

  const handleSubmit = useCallback(
    (formData: BulkEditKey) => {
      if (
        validateBulkDates(
          formData,
          editable,
          fields,
          getValues,
          setValidationErrors,
        )
      ) {
        bulkUpdateKeys(formData, editable, fields, setValue);
        onClose();
      }
    },
    [editable, fields, onClose, getValues, setValue],
  );

  const defaultValue = useMemo(
    () => createDefaultBulkEdit(indicatorStatus, recipientIds),
    [indicatorStatus, recipientIds],
  );
  const updateChangedFields = useCallback(
    (field: EditableField, selected: boolean) => {
      setFields((prev) => {
        const newFields = new Set(prev);
        if (selected) {
          newFields.add(field);
        } else {
          newFields.delete(field);
        }

        return newFields;
      });
    },
    [],
  );

  useEffect(() => {
    setSchema(buildBulkSchema(fields, indicatorStatus));
  }, [fields, indicatorStatus]);

  return (
    <Form
      schema={schema}
      defaultValues={defaultValue}
      onSubmit={handleSubmit}
      serverErrors={validationErrors}
      idAttributePrefix="bulkEdit-requiredKeys"
    >
      <KeyBulkEditForm
        count={editable.size}
        fields={fields}
        indicatorStatus={indicatorStatus}
        onClose={onClose}
        onChangeFields={updateChangedFields}
        recipientIds={recipientIds}
      />
    </Form>
  );
}

export const KeyBulkEditForm = ({
  count,
  fields,
  indicatorStatus,
  onClose,
  onChangeFields,
  recipientIds,
}: Pick<KeyBulkEditPanelProps, 'indicatorStatus' | 'onClose'> & {
  count: number;
  fields: Set<EditableField>;
  onChangeFields: (field: EditableField, selected: boolean) => void;
  recipientIds: number[];
}) => {
  const { t } = useTranslation();
  const { isDirty } = useFormState();

  const handleCancel = useCallback(() => {
    if (isDirty) {
      confirmPanelFormClose(onClose);
    } else {
      onClose();
    }
  }, [onClose, isDirty]);

  return (
    <SidePanel
      minWidth={700}
      side="right"
      collapsedWidth={700}
      width={1786}
      mode="modal"
    >
      <SidePanelWrapper>
        <Padding>
          <Flex align="end" justify="space-between">
            <h4>{t('bulk.title', { count })}</h4>
          </Flex>
        </Padding>
        <Divider style={{ margin: 0 }} />
        <FormWrapper vertical>
          <Scrollable>
            <Padding>
              <KeyBulkEditFormInner
                fields={fields}
                indicatorStatus={indicatorStatus}
                recipientIds={recipientIds}
                onChangeFields={onChangeFields}
              />
            </Padding>
          </Scrollable>
        </FormWrapper>
        <Main.Footer>
          <FullWidthFlex justify="end" gap="small">
            <Button id="cancel-key-bulk-edit" onClick={handleCancel}>
              {t('button.cancel')}
            </Button>
            <FormSubmitButton disabled={fields.size === 0}>
              {t('button.ok')}
            </FormSubmitButton>
          </FullWidthFlex>
        </Main.Footer>
      </SidePanelWrapper>
    </SidePanel>
  );
};

function KeyBulkEditFormInner({
  fields,
  indicatorStatus,
  onChangeFields,
  recipientIds,
}: {
  fields: Set<EditableField>;
  indicatorStatus?: boolean;
  onChangeFields: (field: EditableField, selected: boolean) => void;
  recipientIds: number[];
}) {
  const { t } = useTranslation();
  const { recipientClassifiers, statusClassifiers, ruleClassifiers } =
    useKeyClassifiers();
  const indicatorA =
    useSystemClassifierItem('INDICATOR_STATUS', 'A')?.id ?? null;
  const { setValue } = useFormContext();
  const recipientOptions = useMemo(
    () =>
      recipientClassifiers.filter((option) =>
        recipientIds.includes(Number(option.value)),
      ),
    [recipientIds, recipientClassifiers],
  );
  const hasValidFrom = fields.has('validFrom');
  const hasValidTo = fields.has('validTo');

  const getIdFromName = useGetIdFromName();

  const ruleTypeId = useWatch({ name: 'ruleTypeId' });
  const statusId = useWatch({ name: 'statusId' });
  const statusCode = statusClassifiers.find(
    (status) => Number(status.value) === statusId,
  )?.code;
  const isEmptyValueRuleAllowed =
    !indicatorStatus || statusCode === 'L' || statusCode === 'M';

  useEffect(() => {
    if (!ruleTypeId) return;

    const ruleCode = ruleClassifiers.find(
      (r) => Number(r.value) === Number(ruleTypeId),
    )?.code;
    if (!ruleCode) return;

    // Clear ruleTypeId if it's selected and not allowed
    if (ruleCode === 'EMPTY_VALUE' && !isEmptyValueRuleAllowed) {
      setValue('ruleTypeId', null);
    }
  }, [isEmptyValueRuleAllowed, ruleClassifiers, ruleTypeId, setValue]);

  const handleRuleTypeFilter = useCallback(
    (option: { code: string }) =>
      option.code === 'EMPTY_VALUE' ? isEmptyValueRuleAllowed : true,
    [isEmptyValueRuleAllowed],
  );

  return (
    <Flex vertical id="key-edit-wrapper">
      <div className="key-section">
        <Flex>
          <Checkbox
            id={getIdFromName('block.status')}
            onChange={(e) => {
              onChangeFields('statusId', e.target.checked);
              onChangeFields('ruleTypeId', e.target.checked);
              if (e.target.checked) {
                setValue('statusId', indicatorA, { shouldDirty: true });
              }
            }}
            checked={fields.has('statusId')}
          >
            {t('block.status')}
          </Checkbox>
          <Checkbox
            id={getIdFromName('block.validFrom')}
            onChange={(e) => {
              onChangeFields('validFrom', e.target.checked);
            }}
            checked={hasValidFrom}
          >
            {t('block.validFrom')}
          </Checkbox>
          <Checkbox
            id={getIdFromName('block.validFrom')}
            onChange={(e) => {
              onChangeFields('validTo', e.target.checked);
            }}
            checked={hasValidTo}
          >
            {t('block.validTo')}
          </Checkbox>
          <Checkbox
            id={getIdFromName('block.recipients')}
            onChange={(e) => {
              onChangeFields('recipientIds', e.target.checked);
            }}
            checked={fields.has('recipientIds')}
          >
            {t('block.recipients')}
          </Checkbox>
        </Flex>
      </div>
      {fields.has('statusId') && (
        <>
          <Bleed direction="horizontal">
            <Divider />
          </Bleed>
          <div className="key-section">
            {indicatorStatus && (
              <FormField
                name="statusId"
                label={t('fields.statusId.label')}
                required
              >
                <FormRadioGroup name="statusId" options={statusClassifiers} />
              </FormField>
            )}
            <FormField name="ruleTypeId" label={t('fields.ruleTypeId.label')}>
              <SystemClassifierFormSelect
                allowClear
                code={
                  indicatorStatus
                    ? 'IND_STATUS_RULE_TYPE'
                    : 'IND_VALUE_RULE_TYPE'
                }
                name="ruleTypeId"
                filterFn={handleRuleTypeFilter}
              />
            </FormField>
          </div>
        </>
      )}
      {(hasValidFrom || hasValidTo) && (
        <>
          <Bleed direction="horizontal">
            <Divider />
          </Bleed>
          <div className="key-section">
            <KeyValidityFields validFrom={hasValidFrom} validTo={hasValidTo} />
          </div>
        </>
      )}
      {fields.has('recipientIds') && (
        <>
          <Bleed direction="horizontal">
            <Divider />
          </Bleed>
          <div className="key-section">
            <FormField
              name="recipientIds"
              label={t('fields.recipientIds.label')}
              required
            >
              <FormSelect
                name="recipientIds"
                mode="multiple"
                options={recipientOptions}
              />
            </FormField>
          </div>
        </>
      )}
    </Flex>
  );
}

interface KeyValidityFieldsProps {
  validFrom: boolean;
  validTo: boolean;
}

function KeyValidityFields({ validFrom, validTo }: KeyValidityFieldsProps) {
  const { t } = useTranslation();

  return (
    <Flex gap="middle">
      {validFrom && (
        <DateWrapper>
          <FormField name="validFrom" label={t('fields.validFrom.label')}>
            <FormDatePicker name="validFrom" />
          </FormField>
        </DateWrapper>
      )}
      {validTo && (
        <DateWrapper>
          <FormField name="validTo" label={t('fields.validTo.label')}>
            <FormDatePicker name="validTo" />
          </FormField>
        </DateWrapper>
      )}
    </Flex>
  );
}

const FormWrapper = styled(Flex)`
  overflow-y: hidden;
`;

const FullWidthFlex = styled(Flex)`
  width: 100%;
`;

const DateWrapper = styled.div`
  flex: 0 0 calc(50% - 1rem);
`;

const Scrollable = styled.div`
  overflow-y: auto;
`;

const SidePanelWrapper = styled.div`
  display: grid;
  grid-template-rows: auto auto 1fr 60px;
  height: 100%;
  width: 100%;
  overflow-y: hidden;
  border-left: 1px solid ${token.colorBorderSecondary};
`;
